/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Avatar from '@ion/dls/src/components/avatar/Avatar';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {dlsAlt, dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import DesktopUserProfileView from './DesktopUserProfileView';
import styles from './DesktopHeader.module.scss';

export interface DesktopHeaderContentTypes {
  onHelp: Function;
  username: string;
  onLogout: () => void;
  isLoggingOut: boolean;
  isSelected?: boolean;
}

const useStyles = makeStyles(theme => ({
  avatarWrapper : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    padding        : '0 2px',
    cursor         : 'pointer'
  },
  ionLink : {
    marginRight : '8px',
    ...dlsAlt('components', 'ionToggle', 'ionBeta')
  },
  betaLink : {
    backgroundColor : dlsColor('caBlue'),
    borderRadius    : '10px',
    ...dlsAlt('components', 'ionpreviewText', 'new')
  },
  toggleLink : {
    marginRight : '65px',
    '& > *'     : {
      padding : '0 5px'
    }
  },
  supLink : {
    position : 'relative',
    top      : '-0.5em',
    left     : '0.8em'
  },
  badge : {
    ...dlsAlt('components', 'badge')
  },
  user : {
    display    : 'block',
    fontSize   : '16px',
    lineHeight : '20px',
    color      : dlsColor('grey1'),
    fontWeight : 500
  },
  options : {
    fontSize   : '14px',
    lineHeight : '20px',
    color      : dlsColor('grey2'),
    fontWeight : 400,
    cursor     : 'pointer'
  },
  account : {
    lineHeight : '20px',
    fontSize   : '14px',
    color      : dlsColor('grey3'),
    fontWeight : 400
  }
}));

export const toggleTooltip = makeStyles((theme) => ({
  arrow : {
    color : dlsColor('grey1')
  },
  tooltip : {
    ...dlsAlt('components', 'tooltip')
  }
}));

const ToggleTooltip = function(props) {
  return <Tooltip arrow {...props} />;
};

export const IconCustomTooltip = function(props) {
  return <Tooltip arrow {...props} />;
};

/**
 * The Desktop Header Content component when viewing in Desktop Mode.
 */
export const DesktopHeaderContent = ({username, isLoggingOut, onLogout}: DesktopHeaderContentTypes) => {
  const classes   = useStyles();
  const [isUserProfileView, setIsUserProfileView] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl) && isUserProfileView;
  const id = open ? 'simple-popover' : undefined;

  const handleClick = function(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setIsUserProfileView(!isUserProfileView);
  };

  return (
    <div className={styles.icons} style={{flexGrow: 1}}>
      <div className={classes.avatarWrapper}>
        <div>
          <Avatar name={username} onClick={handleClick} />
        </div>
      </div>
      <label onClick={handleClick} style={{...dlsTypography('body1')}}
        className={styles.username}>{username}
      </label>
      {isUserProfileView &&
        <DesktopUserProfileView isLoggingOut={isLoggingOut}
          open={open}
          id={id}
          anchorEl={anchorEl}
          handleClick={handleClick}
          username={username}
          onLogout={onLogout} />
      }
    </div>
  );
};

export default DesktopHeaderContent;
